/**
 * LawFirmManagement Component
 * Admin interface for managing law firms in the multi-tenant system
 */
import React, { useState, useRef } from 'react';
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  Check,
  X,
  AlertCircle,
  RefreshCw,
  Users,
  Globe,
  ShieldCheck,
  FileText,
  Upload,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../common';
import { Text } from '../../../common/Typography';
import { useLawFirms, useCreateFirm, useUpdateFirm, useSeedMarkoTM, useUploadAgreement, useRemoveAgreement } from '../../../hooks/queries/useLawFirms';

const LawFirmManagement = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFirm, setEditingFirm] = useState(null);
  const [expandedFirm, setExpandedFirm] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);

  // TanStack Query hooks
  const { data: firmsData, isLoading: loading, error: queryError, refetch } = useLawFirms({ activeOnly: false });
  const seedMutation = useSeedMarkoTM();
  const createMutation = useCreateFirm();
  const updateMutation = useUpdateFirm();
  const uploadAgreementMutation = useUploadAgreement();
  const removeAgreementMutation = useRemoveAgreement();

  const firms = firmsData?.firms || [];
  const error = queryError?.message || seedMutation.error?.message || uploadAgreementMutation.error?.message || null;

  const handleSeedMarkoTM = () => {
    seedMutation.mutate();
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const isInternal = type === 'internal';
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${isInternal ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
        {isInternal ? 'Internal' : 'External'}
      </span>
    );
  };

  const getAccessBadge = (accessLevel) => {
    const isFull = accessLevel === 'full';
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${isFull ? 'bg-teal-100 text-teal-800' : 'bg-orange-100 text-orange-800'}`}>
        {isFull ? 'Full Access' : 'Assigned Only'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-[#00796b]" />
      </div>
    );
  }

  const hasMarkoTM = firms.some(f => f.id === 'firm_markotm' || f.type === 'internal');
  const seeding = seedMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Law Firms</h2>
          <Text className="text-gray-500">Manage law firms for the multi-tenant attorney system</Text>
        </div>
        <div className="flex gap-3">
          {!hasMarkoTM && (
            <button
              onClick={handleSeedMarkoTM}
              disabled={seeding}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {seeding ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <ShieldCheck className="h-4 w-4" />
              )}
              Seed MarkoTM
            </button>
          )}
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#004d40] hover:bg-[#00695c] text-white rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Law Firm
          </button>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <Text className="text-red-700">{error}</Text>
          <button
            onClick={() => {
              seedMutation.reset();
              createMutation.reset();
              updateMutation.reset();
              uploadAgreementMutation.reset();
              removeAgreementMutation.reset();
            }}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Firms List */}
      {firms.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Law Firms</h3>
            <Text className="text-gray-500 mb-4">
              Get started by seeding MarkoTM as the internal firm.
            </Text>
            <button
              onClick={handleSeedMarkoTM}
              disabled={seeding}
              className="flex items-center gap-2 px-4 py-2 bg-[#004d40] hover:bg-[#00695c] text-white rounded-lg transition-colors mx-auto disabled:opacity-50"
            >
              {seeding ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <ShieldCheck className="h-4 w-4" />
              )}
              Seed MarkoTM
            </button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {firms.map((firm) => {
            const isExpanded = expandedFirm === firm.id;
            const agreements = firm.agreements || [];

            return (
              <Card key={firm.id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${firm.type === 'internal' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                        <Building2 className={`h-6 w-6 ${firm.type === 'internal' ? 'text-blue-600' : 'text-purple-600'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{firm.name}</h3>
                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                          <Globe className="h-4 w-4" />
                          <span>{firm.id}</span>
                        </div>
                        {firm.primaryContact && (
                          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                            <Users className="h-4 w-4" />
                            <span>{firm.primaryContact.name} - {firm.primaryContact.email}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          {getTypeBadge(firm.type)}
                          {getStatusBadge(firm.status)}
                          {getAccessBadge(firm.accessLevel)}
                          {agreements.length > 0 && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                              {agreements.length} agreement{agreements.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setExpandedFirm(isExpanded ? null : firm.id)}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        title={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => setEditingFirm(firm)}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Section - Agreements */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-700">Agreements</h4>
                        <button
                          onClick={() => setUploadingFor(firm.id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[#004d40] hover:bg-[#00695c] text-white rounded-lg transition-colors"
                        >
                          <Upload className="h-3.5 w-3.5" />
                          Upload
                        </button>
                      </div>

                      {agreements.length === 0 ? (
                        <div className="text-center py-6 bg-gray-50 rounded-lg">
                          <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <Text className="text-gray-500 text-sm">No agreements uploaded yet</Text>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {agreements.map((agreement) => (
                            <div
                              key={agreement.id}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <FileText className="h-5 w-5 text-gray-500" />
                                <div>
                                  <div className="font-medium text-sm text-gray-900">{agreement.name}</div>
                                  <div className="text-xs text-gray-500">
                                    Uploaded {new Date(agreement.uploadedAt).toLocaleDateString()}
                                    {agreement.signedAt && (
                                      <span className="ml-2 text-green-600">
                                        • Signed {new Date(agreement.signedAt).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <a
                                  href={agreement.blobUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
                                  title="View"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                                <button
                                  onClick={() => {
                                    if (window.confirm('Remove this agreement?')) {
                                      removeAgreementMutation.mutate({
                                        firmId: firm.id,
                                        agreementId: agreement.id
                                      });
                                    }
                                  }}
                                  disabled={removeAgreementMutation.isPending}
                                  className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                  title="Remove"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || editingFirm) && (
        <LawFirmModal
          firm={editingFirm}
          onClose={() => {
            setShowAddModal(false);
            setEditingFirm(null);
          }}
          onSave={(data) => {
            if (editingFirm) {
              updateMutation.mutate(
                { id: editingFirm.id, data },
                {
                  onSuccess: () => {
                    setShowAddModal(false);
                    setEditingFirm(null);
                  }
                }
              );
            } else {
              createMutation.mutate(data, {
                onSuccess: () => {
                  setShowAddModal(false);
                  setEditingFirm(null);
                }
              });
            }
          }}
          isPending={createMutation.isPending || updateMutation.isPending}
          mutationError={createMutation.error?.message || updateMutation.error?.message}
        />
      )}

      {/* Agreement Upload Modal */}
      {uploadingFor && (
        <AgreementUploadModal
          firmId={uploadingFor}
          onClose={() => setUploadingFor(null)}
          onUpload={(data) => {
            uploadAgreementMutation.mutate(
              { firmId: uploadingFor, data },
              {
                onSuccess: () => {
                  setUploadingFor(null);
                }
              }
            );
          }}
          isPending={uploadAgreementMutation.isPending}
          mutationError={uploadAgreementMutation.error?.message}
        />
      )}
    </div>
  );
};

/**
 * Modal for adding/editing law firms
 */
const LawFirmModal = ({ firm, onClose, onSave, isPending, mutationError }) => {
  const [formData, setFormData] = useState({
    name: firm?.name || '',
    type: firm?.type || 'external',
    status: firm?.status || 'active',
    accessLevel: firm?.accessLevel || 'assigned_only',
    primaryContact: {
      name: firm?.primaryContact?.name || '',
      email: firm?.primaryContact?.email || ''
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const saving = isPending;
  const error = mutationError;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {firm ? 'Edit Law Firm' : 'Add Law Firm'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {error}
            </div>
          )}

          {!firm && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-700 text-sm">
              A Clerk organization will be automatically created for this law firm.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Firm Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
              placeholder="Law Firm Name"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({
                  ...formData,
                  type: e.target.value,
                  accessLevel: e.target.value === 'internal' ? 'full' : 'assigned_only'
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
                disabled={!!firm}
              >
                <option value="external">External</option>
                <option value="internal">Internal</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Access Level
            </label>
            <select
              value={formData.accessLevel}
              onChange={(e) => setFormData({ ...formData, accessLevel: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
            >
              <option value="assigned_only">Assigned Only (can only see assigned apps)</option>
              <option value="full">Full Access (can see all apps)</option>
            </select>
          </div>

          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Primary Contact</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.primaryContact.name}
                  onChange={(e) => setFormData({
                    ...formData,
                    primaryContact: { ...formData.primaryContact, name: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
                  placeholder="Contact Name"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.primaryContact.email}
                  onChange={(e) => setFormData({
                    ...formData,
                    primaryContact: { ...formData.primaryContact, email: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
                  placeholder="contact@lawfirm.com"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#004d40] hover:bg-[#00695c] text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {firm ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Modal for uploading agreements
 */
const AgreementUploadModal = ({ firmId, onClose, onUpload, isPending, mutationError }) => {
  const [agreementName, setAgreementName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [signedAt, setSignedAt] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        alert('Invalid file type. Please upload a PDF, PNG, JPEG, DOC, or DOCX file.');
        return;
      }
      // Validate file size (20MB)
      if (file.size > 20 * 1024 * 1024) {
        alert('File too large. Maximum size is 20MB.');
        return;
      }
      setSelectedFile(file);
      // Auto-fill name from filename if empty
      if (!agreementName) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setAgreementName(nameWithoutExt);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile || !agreementName) return;

    // Convert file to base64
    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = reader.result;
      onUpload({
        name: agreementName,
        fileData: base64Data,
        contentType: selectedFile.type,
        signedAt: signedAt || null
      });
    };
    reader.readAsDataURL(selectedFile);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Upload Agreement</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {mutationError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {mutationError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Agreement Name *
            </label>
            <input
              type="text"
              value={agreementName}
              onChange={(e) => setAgreementName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
              placeholder="e.g., Service Agreement 2025"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Agreement File *
            </label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              className="hidden"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#00796b] transition-colors text-center"
            >
              {selectedFile ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText className="h-5 w-5 text-[#00796b]" />
                  <span className="text-gray-700">{selectedFile.name}</span>
                  <span className="text-gray-500 text-sm">
                    ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
              ) : (
                <div>
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <Text className="text-gray-500">Click to select a file</Text>
                  <Text className="text-gray-400 text-xs mt-1">PDF, PNG, JPEG, DOC, DOCX (max 20MB)</Text>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Signed Date (optional)
            </label>
            <input
              type="date"
              value={signedAt}
              onChange={(e) => setSignedAt(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave empty if the agreement is pending signature
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || !selectedFile || !agreementName}
              className="flex items-center gap-2 px-4 py-2 bg-[#004d40] hover:bg-[#00695c] text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {isPending ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Upload
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LawFirmManagement;
